import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Fade,
    CircularProgress,
    Paper,
    IconButton
} from "@mui/material";
import { ChatBubble, Refresh, Close } from "@mui/icons-material";
import OTPInput from "react18-otp-input";
import { appColors } from "../../constants/theme";

const RESEND_TIMEOUT = 30;

// Telefon numarasını maskele: +90 *** *** 56 56 (son 4 hane görünsün)
const maskPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Telefon numarasından sadece rakamları al
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 4) return `+90 *** *** __ __`;
    const last4 = digits.slice(-4);
    return `+90 *** *** ${last4.slice(0, 2)} ${last4.slice(2, 4)}`;
};

// Zamanı formatla: 30 -> "00:30" (mobildeki gibi)
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const RegisterPhoneVerification = ({
    phoneNumber,
    onBack,
    onVerify,
    onResendCode,
    loading = false,
    title,
    subtitle,
    verifyButtonText = "Doğrula"
}) => {
    const [otp, setOtp] = useState("");
    const [timeLeft, setTimeLeft] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);

    // İlk açılışta geri sayımı başlat
    useEffect(() => {
        setTimeLeft(RESEND_TIMEOUT);
    }, []);

    // Geri sayım timer'ı
    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [timeLeft]);

    const handleOtpChange = (value) => {
        const digitsOnly = String(value ?? "").replace(/\D/g, "").slice(0, 6);
        setOtp(digitsOnly);
        // Sadece 6 haneli kod tamamlandığında otomatik doğrula
        if (digitsOnly.length === 6 && !loading) {
            console.log('[RegisterPhoneVerification] 6 haneli kod tamamlandı, doğrulama başlatılıyor...');
            const maybePromise = onVerify(digitsOnly);
            if (maybePromise && typeof maybePromise.then === 'function') {
                maybePromise.catch((err) => {
                    console.log('[RegisterPhoneVerification] Doğrulama hatası (auto):', err);
                });
            }
        }
    };

    const handleResend = async () => {
        if (timeLeft > 0 || resendLoading) return;

        setResendLoading(true);
        try {
            await onResendCode();
            setTimeLeft(RESEND_TIMEOUT);
        } catch (error) {
            console.error('Resend code error:', error);
            // Timeout hatası kontrolü - parent component'te de gösteriliyor ama burada da kontrol edelim
            const isTimeoutError = error?.message?.toLowerCase().includes('timeout') ||
                error?.name === 'TimeoutError' ||
                error?.message === 'Timeout' ||
                error?.toString().toLowerCase().includes('timeout') ||
                error?.code === 'auth/timeout';
            
            // Timeout hataları parent component'te gösteriliyor, burada sadece log tutuyoruz
            if (!isTimeoutError) {
                // Timeout dışındaki hatalar için parent component'te gösteriliyor
            }
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Fade in timeout={400}>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.55)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                    padding: "1rem",
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget && !loading) onBack?.();
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: "480px",
                        borderRadius: "24px",
                        overflow: "hidden",
                        background: appColors.white,
                        position: "relative",
                        boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header Section - Green */}
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                            padding: "2rem 2rem 1.5rem 2rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                            position: "relative",
                            minHeight: "120px"
                        }}
                    >
                        <IconButton
                            onClick={onBack}
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                color: appColors.white,
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                                },
                                transition: "all 0.2s ease",
                                zIndex: 10,
                            }}
                        >
                            <Close sx={{ fontSize: 20 }} />
                        </IconButton>

                        {/* Logo placeholder */}
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "10px",
                                background: "rgba(255, 255, 255, 0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: appColors.white,
                                mb: 1
                            }}
                        >
                            <ChatBubble sx={{ fontSize: 20 }} />
                        </Box>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: appColors.white,
                                    fontWeight: 700,
                                    fontSize: "1.5rem",
                                    lineHeight: 1.2,
                                    marginBottom: "0.25rem"
                                }}
                            >
                                Medilya
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: appColors.white,
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    margin: 0,
                                    opacity: 0.9
                                }}
                            >
                                Uzman Kayıt
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: appColors.white,
                                    fontSize: "0.75rem",
                                    margin: 0,
                                    opacity: 0.8,
                                    marginTop: "0.5rem"
                                }}
                            >
                                Danışan takip sisteminize hızlı kayıt
                            </Typography>
                        </Box>
                    </Box>

                    {/* Main Form Section - White */}
                    <Box
                        sx={{
                            padding: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "1.5rem"
                        }}
                    >
                        {/* Phone Number Display */}
                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: appColors.gray900,
                                    textAlign: "center",
                                    mb: 1,
                                    fontWeight: 500,
                                }}
                            >
                                <Typography component="span" sx={{ fontWeight: 700 }}>
                                    {maskPhoneForDisplay(phoneNumber)}
                                </Typography> numarasına SMS gönderildi
                            </Typography>
                        </Box>

                        {/* Verification Code Label */}
                        <Typography
                            variant="body1"
                            sx={{
                                color: appColors.gray900,
                                textAlign: "center",
                                mb: 1,
                                fontWeight: 500,
                            }}
                        >
                            Doğrulama Kodu
                        </Typography>

                        {/* OTP Input */}
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                            <OTPInput
                                value={otp}
                                onChange={handleOtpChange}
                                numInputs={6}
                                isInputNum
                                separator={<span style={{ width: "8px" }} />}
                                inputStyle={{
                                    width: "48px",
                                    height: "48px",
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    border: `2px solid ${appColors.gray200}`,
                                    background: appColors.white,
                                    color: appColors.gray900,
                                    transition: "all 0.2s ease"
                                }}
                                focusStyle={{
                                    border: `2px solid ${appColors.primary}`,

                                }}
                                disabled={loading || resendLoading}
                            />
                        </Box>

                        {/* Buttons Container - Mobildeki gibi: Yeniden Gönder üstte, Doğrula altta */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {/* Resend Code Button - Üstte */}
                            <Button
                                onClick={handleResend}
                                disabled={timeLeft > 0 || resendLoading}
                                variant="outlined"
                                fullWidth
                                startIcon={resendLoading ? null : <Refresh />}
                                sx={{
                                    borderRadius: "12px",
                                    padding: "0.875rem",
                                    textTransform: "none",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    color: timeLeft > 0 ? appColors.gray500 : appColors.primary,
                                    borderColor: timeLeft > 0 ? appColors.gray200 : appColors.primary,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        color: appColors.primaryDark,
                                        borderColor: appColors.primaryDark,
                                        background: "rgba(13, 148, 136, 0.1)",
                                    },
                                    "&:disabled": {
                                        color: appColors.gray200,
                                        borderColor: appColors.gray200,
                                    }
                                }}
                            >
                                {resendLoading ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Kod Gönderiliyor
                                    </>
                                ) : timeLeft > 0 ? (
                                    `Kodu Tekrar Gönder (${formatTime(timeLeft)})`
                                ) : (
                                    "Kodu Tekrar Gönder"
                                )}
                            </Button>

                            {/* Verify Button - Altta */}
                            <Button
                                onClick={() => {
                                    const p = onVerify(otp);
                                    if (p && typeof p.then === 'function') {
                                        p.catch((err) => {
                                            console.log('[RegisterPhoneVerification] Doğrulama hatası (button):', err);
                                        });
                                    }
                                }}
                                disabled={otp.length !== 6 || loading || resendLoading}
                                variant="contained"
                                fullWidth
                                sx={{
                                    borderRadius: "12px",
                                    padding: "0.875rem",
                                    background: appColors.primary,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "1rem",

                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        background: appColors.primaryDark,

                                    },
                                    "&:disabled": {
                                        background: appColors.gray200,
                                        color: appColors.gray500,
                                        transform: "none",

                                    }
                                }}
                            >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: "white" }} />
                            ) : (
                                "Doğrula"
                            )}
                            </Button>
                        </Box>

                        {/* Security Information */}
                        <Box
                            sx={{
                                marginTop: "1rem",
                                padding: "1rem",
                                background: appColors.bgMuted,
                                borderRadius: "12px",
                                textAlign: "center"
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: appColors.gray500,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.75rem"
                                }}
                            >
                                Güvenli bağlantı ile korunmaktadır
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                    <Box sx={{ fontSize: 16, color: appColors.primary }}>🔒</Box>
                                    <Typography variant="caption" sx={{ color: appColors.primary, fontWeight: 600 }}>
                                        SSL Güvenlik
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                    <Box sx={{ fontSize: 16, color: appColors.gray500 }}>🔒</Box>
                                    <Typography variant="caption" sx={{ color: appColors.gray500, fontWeight: 600 }}>
                                        256-bit Şifreleme
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Fade>
    );
};

export default RegisterPhoneVerification;
